/**
 * Typed WebSocket client skeleton (F0).
 *
 * Handles connect / subscribe / onEvent / close with exponential backoff and
 * jittered reconnect. It parses inbound text frames into EventEnvelopeV1 and
 * dispatches to per-event-type and wildcard listeners. Inbound binary frames
 * are surfaced raw via onBinary. Outbound audio uplink (F1) is sent with
 * `sendBinary`, which drops frames while the socket is not OPEN.
 */

import {
  type EventEnvelopeV1,
  type EventType,
  isEventEnvelopeV1,
} from '@/shared/models/events';

export const WS_BASE: string = resolveWsBase();

/**
 * Resolve the WebSocket base URL.
 *
 * Priority: explicit `VITE_WS_BASE` → derived from `VITE_API_BASE`
 * (http→ws, https→wss) → same-origin fallback.
 *
 * The derivation matters: the SPA is served from the frontend origin (e.g.
 * :5173) but the WS endpoints live on the BACKEND origin (e.g. :8000). Without
 * this, `new WebSocket('/ws/host/...')` resolves against the page origin and
 * the connection fails. `VITE_API_BASE` is already the backend origin the REST
 * client uses, so we reuse it and just swap the scheme.
 */
function resolveWsBase(): string {
  const explicit = import.meta.env.VITE_WS_BASE as string | undefined;
  if (explicit) return explicit.replace(/\/+$/, '');

  const apiBase = import.meta.env.VITE_API_BASE as string | undefined;
  if (apiBase) {
    return apiBase.replace(/^http(s?):\/\//i, (_m, s: string) => (s ? 'wss://' : 'ws://')).replace(
      /\/+$/,
      '',
    );
  }

  // Same-origin fallback (works only when the backend shares the page origin).
  if (typeof window !== 'undefined' && window.location) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}`;
  }
  return '';
}

export type WsStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'reconnecting';

export type EventListener<K extends EventType = EventType> = (
  event: Extract<EventEnvelopeV1, { event_type: K }>,
) => void;

type AnyEventListener = (event: EventEnvelopeV1) => void;

export interface WsClientOptions {
  /** Full URL or path relative to WS_BASE (e.g. `/ws/viewer/{token}`). */
  url: string;
  /** Initial reconnect delay in ms (default 500). */
  baseDelayMs?: number;
  /** Max reconnect delay in ms (default 15000). */
  maxDelayMs?: number;
  /** Max reconnect attempts before giving up (default Infinity). */
  maxRetries?: number;
  onStatus?: ((status: WsStatus) => void) | undefined;
  onBinary?: ((data: ArrayBuffer | Blob) => void) | undefined;
  onError?: ((error: Event) => void) | undefined;
  /**
   * Called on every socket close with the close code/reason. Return `false` to
   * VETO the automatic reconnect (e.g. an application-level "do not retry" code
   * such as 4404 link-invalid). Returning `true`/`undefined` allows the normal
   * backoff reconnect to proceed. Not called for a manual `close()`.
   */
  onClose?: ((code: number, reason: string) => boolean | void) | undefined;
}

export class WsClient {
  private readonly opts: Required<
    Pick<WsClientOptions, 'baseDelayMs' | 'maxDelayMs' | 'maxRetries'>
  > &
    WsClientOptions;

  private socket: WebSocket | null = null;
  private status: WsStatus = 'idle';
  private retries = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private manuallyClosed = false;

  private readonly typedListeners = new Map<EventType, Set<AnyEventListener>>();
  private readonly wildcardListeners = new Set<AnyEventListener>();

  constructor(options: WsClientOptions) {
    this.opts = {
      baseDelayMs: 500,
      maxDelayMs: 15_000,
      maxRetries: Number.POSITIVE_INFINITY,
      ...options,
    };
  }

  get currentStatus(): WsStatus {
    return this.status;
  }

  /** True only when the underlying socket is OPEN and ready to send. */
  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  connect(): void {
    this.manuallyClosed = false;
    this.open();
  }

  private open(): void {
    this.clearReconnectTimer();
    this.setStatus(this.retries === 0 ? 'connecting' : 'reconnecting');

    const url = this.opts.url.startsWith('ws')
      ? this.opts.url
      : `${WS_BASE}${this.opts.url}`;

    const socket = new WebSocket(url);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;

    socket.onopen = () => {
      this.retries = 0;
      this.setStatus('open');
    };

    socket.onmessage = (ev: MessageEvent<unknown>) => {
      this.handleMessage(ev.data);
    };

    socket.onerror = (ev: Event) => {
      this.opts.onError?.(ev);
    };

    socket.onclose = (ev: CloseEvent) => {
      this.socket = null;
      if (this.manuallyClosed) {
        this.setStatus('closed');
        return;
      }
      // Give the caller a chance to veto reconnect on an application-level code.
      const allowReconnect = this.opts.onClose?.(ev.code, ev.reason);
      if (allowReconnect === false) {
        this.manuallyClosed = true;
        this.clearReconnectTimer();
        this.setStatus('closed');
        return;
      }
      this.scheduleReconnect();
    };
  }

  private handleMessage(data: unknown): void {
    if (data instanceof ArrayBuffer || data instanceof Blob) {
      this.opts.onBinary?.(data);
      return;
    }
    if (typeof data !== 'string') return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return;
    }
    if (!isEventEnvelopeV1(parsed)) return;

    const envelope = parsed;
    this.wildcardListeners.forEach((fn) => fn(envelope));
    this.typedListeners.get(envelope.event_type)?.forEach((fn) => fn(envelope));
  }

  private scheduleReconnect(): void {
    if (this.retries >= this.opts.maxRetries) {
      this.setStatus('closed');
      return;
    }
    this.setStatus('reconnecting');
    const exp = Math.min(
      this.opts.maxDelayMs,
      this.opts.baseDelayMs * 2 ** this.retries,
    );
    const jitter = Math.random() * exp * 0.3;
    const delay = exp + jitter;
    this.retries += 1;
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /** Subscribe to a single event type. Returns an unsubscribe function. */
  on<K extends EventType>(type: K, listener: EventListener<K>): () => void {
    let set = this.typedListeners.get(type);
    if (!set) {
      set = new Set();
      this.typedListeners.set(type, set);
    }
    const fn = listener as AnyEventListener;
    set.add(fn);
    return () => set?.delete(fn);
  }

  /** Subscribe to every event. Returns an unsubscribe function. */
  onEvent(listener: AnyEventListener): () => void {
    this.wildcardListeners.add(listener);
    return () => this.wildcardListeners.delete(listener);
  }

  /** Send a text/JSON control frame. No-op unless the socket is OPEN. */
  send(data: string): void {
    if (this.isOpen) {
      this.socket?.send(data);
    }
  }

  /**
   * Send a binary audio frame over the uplink (F1). Frames are DROPPED while
   * the socket is not OPEN — audio is real-time, so buffering stale PCM would
   * only add latency on reconnect. Returns true when the frame was handed to
   * the socket.
   */
  sendBinary(data: ArrayBuffer | ArrayBufferView): boolean {
    if (!this.isOpen || !this.socket) return false;
    this.socket.send(data);
    return true;
  }

  close(code?: number, reason?: string): void {
    this.manuallyClosed = true;
    this.clearReconnectTimer();
    this.socket?.close(code, reason);
    this.socket = null;
    this.setStatus('closed');
  }

  private setStatus(status: WsStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.opts.onStatus?.(status);
  }
}
